import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Contact Us Today' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// Feature image bound to a gallery photo so the intro shows a real interior
// shot; editors can swap it, and the sourceBinding keeps image + alt coherent.
const imageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick one appealing feature photo for the top of the Amenities page. Never choose a photo that shows a toilet.',
    context: 'Single feature image in the two-column Amenities intro, beside the headline and CTA.',
    sourceCollection: 'photo_gallery',
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Amenities page intro: a two-column stripe that opens the page below the
 * hero/wave band. Left column carries the headline, an amenities blurb, and a
 * primary CTA; the right column shows a feature photo. Mirrors the intro-first
 * pattern used by FloorPlansIntro / VirtualTourIntro on their pages.
 */
export function AmenitiesIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const cta = useSlotGroup('amenitiesintro.cta', ctaDef);
  const image = useSlotGroup('amenitiesintro.image', imageDef);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] py-12 pb-6 md:py-20"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: copy + CTA */}
          <div>
            <RichTextContent
              path="amenitiesintro.heading"
              label="Heading"
              defaultValue="<p>Luxury Apartment Amenities</p>"
              as="h2"
              className="text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.05] mb-6"
              style={{ color: '#000', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
            />

            <RichTextContent
              path="amenitiesintro.body"
              label="Body Text"
              defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, enjoy amenities designed for your lifestyle. Stay active in our fitness center, relax by the resort-style pool, or unwind in landscaped courtyards. Our pet-friendly community also features a dog park and convenient extras throughout. Experience comfort and convenience at <span data-type="field-token" data-field="property_name"></span>, the perfect place to call home.</p>'
              as="div"
              className="font-body text-base leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-4 [&_a]:text-primary [&_a]:underline"
            />

            <div className="mt-8 flex justify-center md:justify-start">
              <EditableSlotGroup groupPath="amenitiesintro.cta" definition={ctaDef}>
                <a
                  href={cta.url || '/contact-us'}
                  target={cta._target || '_self'}
                  className="ai-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
                >
                  {cta.label || 'Contact Us Today'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right: feature image */}
          <EditableSlotGroup groupPath="amenitiesintro.image" definition={imageDef}>
            <div className="ai-image-wrap">
              {image.image ? (
                <VibesitesImage
                  url={image.image}
                  alt={image.alt || 'Apartment interior'}
                  className="ai-image"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              ) : (
                <div className="ai-image ai-image--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Solid primary pill — matches the site-wide CTA treatment. */
        .ai-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .ai-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        /* Feature image fills its column with a stable landscape ratio. */
        .ai-image-wrap {
          width: 100%;
          overflow: hidden;
          border-radius: 0;
        }
        .ai-image {
          display: block;
          width: 100%;
          aspect-ratio: 4 / 3;
          object-fit: cover;
          object-position: center;
        }
        .ai-image--placeholder {
          background: var(--vibe-color-surface);
        }

        @media (max-width: 768px) {
          .ai-image {
            aspect-ratio: 16 / 10;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesIntro',
  displayName: 'Amenities Intro',
  tokens: {
    'amenitiesintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Luxury Apartment Amenities</p>',
    },
    'amenitiesintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_amenities_blurb',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, enjoy amenities designed for your lifestyle. Stay active in our fitness center, relax by the resort-style pool, or unwind in landscaped courtyards. Our pet-friendly community also features a dog park and convenient extras throughout. Experience comfort and convenience at <span data-type="field-token" data-field="property_name"></span>, the perfect place to call home.</p>',
    },
  },
  slotGroups: {
    'amenitiesintro.cta': ctaDef,
    'amenitiesintro.image': imageDef,
  },
};

(AmenitiesIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesIntro;
